import axios from 'axios';

// Define API base URL from environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://hotels.myabad.com';

// Create axios instance with authentication headers
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add authentication interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // If error is 401 and we haven't already tried to refresh the token
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Get the refresh token from local storage
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          // No refresh token, redirect to login
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try to get a new token
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken
        });

        // If successful, update the tokens
        if (response.data.access) {
          localStorage.setItem('token', response.data.access);
          if (response.data.refresh) {
            localStorage.setItem('refreshToken', response.data.refresh);
          }
          
          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails, redirect to login
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Types
export interface FeedbackRequest {
  id: number;
  reservation_id: number;
  email_template_id: number;
  sent_at: string;
  token: string;
  email_sent_to: string;
  is_completed: boolean;
  guest_name: string;
  property_name: string;
}

export interface FeedbackForward {
  id: number;
  feedback: number;
  forwarded_to_email: string;
  comments: string | null;
  forwarded_at: string;
  forwarded_by: number | null;
  forwarded_by_username: string | null;
}

export interface Feedback {
  id: number;
  overall_rating: number;
  cleanliness_rating: number;
  service_rating: number;
  food_rating: number;
  value_rating: number;
  comments: string | null;
  would_recommend: boolean;
  created_at: string;
  guest_name: string;
  property_name: string;
  guest_email: string;
  guest_phone?: string | null;
  reply: string | null;
  reply_sent_at: string | null;
  replied_by: number | null;
  replied_by_username: string | null;
  forwards: FeedbackForward[];
  forwards_count: number;
}

export interface SendFeedbackRequestParams {
  reservation_id: number;
  email_template_id: number;
}

export interface FeedbackFilter {
  page?: number;
  page_size?: number;
  search?: string;
  rating_min?: number;
  rating_max?: number;
  would_recommend?: boolean;
  property?: string;
  start_date?: string;
  end_date?: string;
  sort?: string;
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface FeedbackListStats {
  total_count: number;
  average_overall_rating: number;
  recommendation_rate: number;
}

// Helper to build query string from filter object
const buildQueryString = (filters: Record<string, any>): string => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  
  return params.toString();
};

// Feedback service
const feedbackService = {
  // Get all feedback requests
  getFeedbackRequests: async (params?: { page?: number; page_size?: number; is_completed?: boolean }): Promise<PaginatedResponse<FeedbackRequest>> => {
    const response = await api.get('/feedback-requests/', { params });
    return response.data;
  },

  // Get a specific feedback request
  getFeedbackRequest: async (id: number): Promise<FeedbackRequest> => {
    const response = await api.get(`/feedback-requests/${id}/`);
    return response.data;
  },

  // Send feedback email using existing API
  sendFeedbackEmail: async (params: SendFeedbackRequestParams): Promise<any> => {
    const response = await api.post('/feedback-requests/send_email/', params);
    return response.data;
  },

  // Get paginated list of all feedback
  getFeedbacks: async (filters: FeedbackFilter = {}): Promise<PaginatedResponse<Feedback>> => {
    const queryString = buildQueryString(filters);
    const response = await api.get(`/feedback/?${queryString}`);
    return response.data;
  },

  /** Aggregates over all feedback visible to the user (same scope as list, not limited by page). */
  getFeedbackStats: async (): Promise<FeedbackListStats> => {
    const response = await api.get('/feedback/stats/');
    return response.data;
  },

  // Get a specific feedback
  getFeedback: async (id: number): Promise<Feedback> => {
    const response = await api.get(`/feedback/${id}/`);
    return response.data;
  },

  // Get feedback for a specific reservation
  getReservationFeedback: async (reservationId: number): Promise<Feedback> => {
    const response = await api.get(`/feedback/${reservationId}/reservation/`);
    return response.data.data;
  },

  // Get all email templates
  getEmailTemplates: async (isActive: boolean = true): Promise<EmailTemplate[]> => {
    const response = await api.get(`/email-templates/?is_active=${isActive}`);
    return response.data;
  },

  // Send feedback request email
  sendFeedbackRequest: async (reservationId: number, emailTemplateId: number): Promise<{ feedback_request_id: number, feedback_url: string }> => {
    const response = await api.post('/feedback-requests/send_email/', {
      reservation_id: reservationId,
      email_template_id: emailTemplateId
    });
    return response.data.data;
  },

  // Submit feedback (public endpoint)
  submitFeedback: async (feedbackData: any): Promise<{ feedback_id: number }> => {
    // This is a public endpoint, so we use axios directly without auth headers
    const response = await axios.post(`${API_BASE_URL}/public/submit-feedback/`, feedbackData);
    return response.data.data;
  },

  // Validate feedback token (public endpoint)
  validateToken: async (token: string): Promise<{ guest_name: string, property_name: string, already_submitted: boolean }> => {
    // This is a public endpoint, so we use axios directly without auth headers
    const response = await axios.get(`${API_BASE_URL}/public/validate-feedback-token/${token}/`);
    return response.data.data;
  },

  // Reply to feedback
  replyToFeedback: async (feedbackId: number, reply: string): Promise<Feedback> => {
    const response = await api.post(`/feedback/${feedbackId}/reply/`, { reply });
    return response.data.data;
  },

  // Forward feedback to department
  forwardFeedback: async (feedbackId: number, forwarded_to_email: string, comments: string): Promise<FeedbackForward> => {
    const response = await api.post(`/feedback/${feedbackId}/forward/`, { 
      forwarded_to_email,
      comments 
    });
    return response.data.data;
  }
};

export default feedbackService; 